// Точки API сервера заказов — по одной функции на действие покупателя.
import { apiRequest, apiUpload } from "./client";
import type {
  AccountDeletionView,
  Catalog,
  CityPriceView,
  CityView,
  DeliveryPrefillView,
  DeliveryQuoteOptions,
  DeliverySettingsView,
  PickupPointView,
  Direction,
  DocumentList,
  DocumentView,
  LogoutView,
  MeView,
  MiniAppLoginResponse,
  ModelFiles,
  OrderSummary,
  OrderView,
  PaymentLink,
  PhoneCodeResponse,
  ProductId,
  TelegramInfo,
  TelegramLinkView,
  TelegramSiteLoginConfig,
} from "./types";

// --- вход ---

export function loginMiniApp(initData: string): Promise<MiniAppLoginResponse> {
  return apiRequest("/auth/telegram/miniapp", { method: "POST", json: { init_data: initData } });
}

export function loginSiteTelegram(
  code: string,
  codeVerifier: string,
  redirectUri: string,
): Promise<MeView> {
  return apiRequest("/auth/telegram/site", {
    method: "POST",
    json: { code, code_verifier: codeVerifier, redirect_uri: redirectUri },
  });
}

export function getTelegramSiteLoginConfig(): Promise<TelegramSiteLoginConfig> {
  return apiRequest("/auth/telegram/site/config");
}

// bySms — покупатель просит код по СМС: код в Telegram не пришёл.
export function requestPhoneCode(phone: string, bySms = false): Promise<PhoneCodeResponse> {
  const json = bySms ? { phone, channel: "sms" } : { phone };
  return apiRequest("/auth/phone/code", { method: "POST", json });
}

export function verifyPhoneCode(phone: string, code: string): Promise<MeView> {
  return apiRequest("/auth/phone/verify", { method: "POST", json: { phone, code } });
}

export function logout(): Promise<LogoutView> {
  return apiRequest("/auth/logout", { method: "POST" });
}

export function getMe(): Promise<MeView> {
  return apiRequest("/me");
}

export function deleteAccount(): Promise<AccountDeletionView> {
  return apiRequest("/me", { method: "DELETE" });
}

// --- Telegram-бот ---

// Ссылка на бота и работает ли поддержка. Без входа.
export function getTelegramInfo(): Promise<TelegramInfo> {
  return apiRequest("/telegram");
}

// «Подключить Telegram» на сайте: одноразовая ссылка на бота.
export function createTelegramLink(): Promise<TelegramLinkView> {
  return apiRequest("/me/telegram/link", { method: "POST" });
}

// Ссылка в бота у заказа: номер заказа, а без подключённого Telegram — и код привязки
// (telegram-continuity).
export function createOrderTelegramLinks(orderId: string): Promise<{ bot_url: string; expires_at: string | null }> {
  return apiRequest(orderPath(orderId, "telegram-links"), { method: "POST" });
}

// Одноразовая ссылка на сайт уже со входом — из Mini App, для файла модели (telegram-continuity).
export function createSiteLink(path: string): Promise<{ url: string; expires_at: string }> {
  return apiRequest("/me/site-link", { method: "POST", json: { path } });
}

export function loginBySiteLink(token: string): Promise<{ path: string }> {
  return apiRequest("/auth/site-link", { method: "POST", json: { token } });
}

// Включить или выключить сообщения о заказах в Telegram — для всей учётки.
export function setNotifications(enabled: boolean): Promise<MeView> {
  return apiRequest("/me/notifications", { method: "PUT", json: { enabled } });
}

// --- документы ---

// Список документов с датами редакций и продавец. Без входа.
export function getDocuments(): Promise<DocumentList> {
  return apiRequest("/documents");
}

// Полный текст документа: заголовок и HTML с сервера.
export function getDocument(documentId: string): Promise<DocumentView> {
  return apiRequest(`/documents/${encodeURIComponent(documentId)}`);
}

// --- каталог и заказы ---

export function getCatalog(): Promise<Catalog> {
  return apiRequest("/catalog");
}

export function listOrders(): Promise<OrderSummary[]> {
  return apiRequest("/orders");
}

export function createOrder(direction: Direction): Promise<OrderView> {
  return apiRequest("/orders", { method: "POST", json: { direction } });
}

// screenVisible — страница видна и в фокусе: покупатель видит заказ сам, и бот не повторит
// ему в Telegram то, что есть в этом ответе.
export function getOrder(orderId: string, { screenVisible = false } = {}): Promise<OrderView> {
  return apiRequest(`/orders/${encodeURIComponent(orderId)}`, {
    headers: screenVisible ? { "X-Screen-Visible": "1" } : undefined,
  });
}

function orderPath(orderId: string, action: string): string {
  return `/orders/${encodeURIComponent(orderId)}/${action}`;
}

// --- фото ---


// consentRevision — дата редакции согласия на обработку фото, которое отметил покупатель.
// У фото к оплаченному заказу её нет: согласие дано при заказе.
export function uploadPhoto(
  orderId: string,
  photo: Blob,
  fileName: string,
  consentRevision: string | null,
  onProgress: (share: number) => void = () => undefined,
): Promise<OrderView> {
  const formData = new FormData();
  formData.append("photo", photo, fileName);
  if (consentRevision !== null) formData.append("consent_revision", consentRevision);
  return apiUpload(orderPath(orderId, "photos"), formData, onProgress);
}

export function removePhoto(orderId: string, photoNumber: number): Promise<OrderView> {
  return apiRequest(orderPath(orderId, `photos/${photoNumber}`), { method: "DELETE" });
}

export function checkPhotos(orderId: string): Promise<OrderView> {
  return apiRequest(orderPath(orderId, "photos/check"), { method: "POST" });
}

// --- образ, концепт, правка ---

export type LookChoice = {
  world?: string;
  race?: string;
  class_id?: string;
  archetype?: string;
  // Свой персонаж миниатюры или образ подарка своими словами.
  description?: string;
  wishes?: string;
};

export function setLook(orderId: string, look: LookChoice): Promise<OrderView> {
  return apiRequest(orderPath(orderId, "look"), { method: "POST", json: look });
}

export function startConcept(orderId: string): Promise<OrderView> {
  return apiRequest(orderPath(orderId, "concept"), { method: "POST" });
}

export function startEdit(orderId: string, text: string): Promise<OrderView> {
  return apiRequest(orderPath(orderId, "edit"), { method: "POST", json: { text } });
}

export function chooseVersion(orderId: string, conceptNumber: number): Promise<OrderView> {
  return apiRequest(orderPath(orderId, "choose-version"), {
    method: "POST",
    json: { concept_number: conceptNumber },
  });
}

export function startPreviews(orderId: string): Promise<OrderView> {
  return apiRequest(orderPath(orderId, "previews"), { method: "POST" });
}

// --- покупка и модель ---

// Даты редакций оферты и условий возврата, с которыми покупатель отметил согласие.
export type OfferRevisions = { offerRevision: string | null; refundRevision: string | null };

// Доставка печати: номер расчёта и получатель. Цену задаёт сервер по расчёту.
export type PurchaseDelivery = { quoteId: string; recipientName: string; recipientPhone: string };

export function choosePurchase(
  orderId: string,
  product: ProductId,
  sizeId: string,
  caseKind: string | null,
  revisions: OfferRevisions,
  delivery: PurchaseDelivery | null = null,
): Promise<OrderView> {
  return apiRequest(orderPath(orderId, "purchase"), {
    method: "POST",
    json: {
      product,
      size_id: sizeId,
      case_kind: caseKind,
      offer_revision: revisions.offerRevision,
      refund_revision: revisions.refundRevision,
      delivery:
        delivery === null
          ? null
          : {
              quote_id: delivery.quoteId,
              recipient_name: delivery.recipientName,
              recipient_phone: delivery.recipientPhone,
            },
    },
  });
}

// --- доставка ---

export function getDeliverySettings(): Promise<DeliverySettingsView> {
  return apiRequest("/delivery/settings");
}

export function searchDeliveryCities(query: string, signal?: AbortSignal): Promise<CityView[]> {
  return apiRequest(`/delivery/cities?${new URLSearchParams({ q: query })}`, { signal });
}

// Пункты города или рамки карты «юг,запад,север,восток».
export function getPickupPoints(
  where: { city: string } | { bbox: string },
  signal?: AbortSignal,
): Promise<PickupPointView[]> {
  return apiRequest(`/delivery/points?${new URLSearchParams(where)}`, { signal });
}

export function getDeliveryPrices(
  orderId: string,
  city: string,
  sizeId: string,
  caseKind: string | null,
): Promise<CityPriceView[]> {
  const query = new URLSearchParams({ city, size_id: sizeId });
  if (caseKind !== null) query.set("case_kind", caseKind);
  return apiRequest(`${orderPath(orderId, "delivery/prices")}?${query}`);
}

export type QuoteTarget =
  | { method: "point"; pointId: string }
  | { method: "courier"; cityGuid: string; street: string; house: string; flat: string };

export function quoteDelivery(
  orderId: string,
  sizeId: string,
  caseKind: string | null,
  target: QuoteTarget,
): Promise<DeliveryQuoteOptions> {
  const json =
    target.method === "point"
      ? { size_id: sizeId, case_kind: caseKind, method: "point", point_id: target.pointId }
      : {
          size_id: sizeId,
          case_kind: caseKind,
          method: "courier",
          city_guid: target.cityGuid,
          street: target.street,
          house: target.house,
          flat: target.flat,
        };
  return apiRequest(orderPath(orderId, "delivery/quote"), { method: "POST", json });
}

export function getDeliveryPrefill(orderId: string): Promise<DeliveryPrefillView> {
  return apiRequest(orderPath(orderId, "delivery/prefill"));
}

export function redoModel(orderId: string, feedback: string | null): Promise<OrderView> {
  return apiRequest(orderPath(orderId, "model/redo"), { method: "POST", json: { feedback } });
}

export function rejectModel(orderId: string, feedback: string | null): Promise<OrderView> {
  return apiRequest(orderPath(orderId, "model/reject"), { method: "POST", json: { feedback } });
}

// «Оплатить»: счёт в кассе на выбор покупки заказа и ссылка на страницу кассы. Пока выбор
// не меняется, сервер отдаёт тот же счёт. Ссылка заглушки кассы — относительная.
export function createPayment(orderId: string): Promise<PaymentLink> {
  return apiRequest(orderPath(orderId, "payment"), { method: "POST" });
}

// «Нравится»: вместе с моделью — её лицо, редакция оферты и текст у кнопки, который видел
// покупатель. Лицо — photo или sketch: у фигуры подарка их два.
export function acceptModel(
  orderId: string,
  model: { attempt: number; face: string },
  seen: { offerRevision: string | null; acceptanceText: string | null },
): Promise<OrderView> {
  return apiRequest(orderPath(orderId, "model/accept"), {
    method: "POST",
    json: {
      attempt: model.attempt,
      face: model.face,
      offer_revision: seen.offerRevision,
      acceptance_text: seen.acceptanceText,
    },
  });
}

// Лицо по фото ещё раз после сбоя сервиса — переделка не тратится.
export function retryPhotoFace(orderId: string): Promise<OrderView> {
  return apiRequest(orderPath(orderId, "model/photo-face"), { method: "POST" });
}

export function getModelFiles(orderId: string): Promise<ModelFiles> {
  return apiRequest(orderPath(orderId, "model/file"));
}
